import { useState } from "react";
import { auth } from "../lib/auth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { KeyRound, LogOut, Lock, ShieldCheck, Users, Zap, FileText, Package } from "lucide-react";
import KeysManager from "../components/KeysManager";

const PLAN_IDS = ["regular", "pro", "elite", "franchise"];

function PinGate({ onAuthed }) {
  const [user, setUser] = useState("owner");
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await auth.post("/owner/login", { username: user, pin });
      toast.success("OWNER_AUTHED");
      onAuthed();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Invalid credentials");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        data-testid="owner-gate"
        className="w-full max-w-sm border border-rose-500/40 bg-[#0E0E0E] p-6 space-y-4"
      >
        <div className="text-[10px] uppercase tracking-[0.2em] text-rose-300 font-mono inline-flex items-center gap-2">
          <Lock size={11} /> OWNER_SUITE
        </div>
        <h2 className="font-mono text-2xl tracking-tight">Authorize.</h2>
        <p className="text-[12px] text-zinc-500 font-mono">
          This area is reserved for the owner. Sessions last 12 hours.
        </p>
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">USERNAME</div>
          <input
            data-testid="owner-username"
            value={user}
            onChange={(e) => setUser(e.target.value)}
            className="mt-1 w-full h-9 px-2 bg-[#0A0A0A] border border-[#27272A] focus:border-rose-400 outline-none text-[12px] font-mono"
          />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">PIN</div>
          <div className="flex items-center border border-[#27272A] focus-within:border-rose-400 mt-1 bg-[#0A0A0A]">
            <KeyRound size={12} className="ml-2 text-zinc-500" />
            <input
              data-testid="owner-pin"
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              autoComplete="off"
              className="flex-1 h-9 px-2 bg-transparent outline-none text-[12px] font-mono"
            />
          </div>
        </label>
        {err && (
          <div data-testid="owner-error" className="border border-rose-500/40 bg-rose-500/10 text-rose-200 text-[11px] font-mono p-2">
            {String(err)}
          </div>
        )}
        <button
          type="submit"
          data-testid="owner-submit"
          disabled={busy}
          className="w-full h-10 bg-rose-400 text-black hover:bg-rose-300 font-mono uppercase text-[12px] tracking-wider disabled:opacity-50 inline-flex items-center justify-center gap-2"
        >
          <ShieldCheck size={12} />
          {busy ? "AUTHENTICATING…" : "ENTER_OWNER_SUITE"}
        </button>

        {/* Developer credentials — visible per Phase 10 spec */}
        <div
          data-testid="owner-dev-credentials"
          className="mt-4 border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] font-mono space-y-2"
        >
          <div className="text-[9px] uppercase tracking-[0.2em] text-amber-300">
            DEV_CREDENTIALS · INTERNAL_ONLY
          </div>
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-3">
              <span className="text-zinc-500">OWNER</span>
              <span className="text-zinc-200 tabnum">
                owner · <span className="text-amber-300">Admin01!</span> · PIN <span className="text-amber-300">(see env OWNER_PIN)</span>
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-zinc-500">PARTNER</span>
              <span className="text-zinc-200 tabnum">
                partner · <span className="text-purple-300">partner01!</span> · <span className="text-zinc-500">no PIN</span>
              </span>
            </div>
          </div>
          <div className="text-[9.5px] text-zinc-500 leading-relaxed pt-1 border-t border-amber-500/20">
            Partner demo logs into the regular app with 15 MindStein usages · resets every login/logout cycle · cannot access this owner suite.
          </div>
        </div>
      </form>
    </div>
  );
}

export default function OwnerPage() {
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: ["owner-me"],
    queryFn: () => auth.get("/owner/me").then((r) => r.data),
    retry: false,
  });
  const stats = useQuery({
    queryKey: ["owner-stats"],
    queryFn: () => auth.get("/owner/stats").then((r) => r.data),
    enabled: !!me.data,
    refetchInterval: 15000,
  });
  const users = useQuery({
    queryKey: ["owner-users"],
    queryFn: () => auth.get("/owner/users").then((r) => r.data),
    enabled: !!me.data,
    refetchInterval: 15000,
  });

  const setPlan = useMutation({
    mutationFn: ({ user_id, plan_id }) =>
      auth.post(`/owner/users/${user_id}/set-plan?plan_id=${plan_id}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["owner-users"] });
      toast.success("PLAN_UPDATED");
    },
  });
  const grant = useMutation({
    mutationFn: ({ user_id, qty }) =>
      auth.post(`/owner/users/${user_id}/grant-uses?qty=${qty}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["owner-users"] });
      toast.success("USES_GRANTED");
    },
  });
  const logout = async () => {
    try {
      await auth.post("/owner/logout");
    } catch { /* ignore */ }
    me.refetch();
  };

  if (me.isLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-zinc-400 font-mono text-[12px] flex items-center justify-center">
        VERIFYING_OWNER_SESSION…
      </div>
    );
  }
  if (!me.data) {
    return <PinGate onAuthed={() => me.refetch()} />;
  }

  return (
    <div data-testid="owner-page" className="min-h-screen bg-[#0A0A0A] text-white p-4 space-y-5">
      <header className="flex items-center gap-4 border-b border-rose-500/30 pb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-rose-300 font-mono inline-flex items-center gap-2">
          <ShieldCheck size={12} /> OWNER_SUITE
        </div>
        <h1 className="font-mono text-2xl tracking-tight">{me.data.username}</h1>
        <button
          data-testid="owner-download-audit"
          onClick={async () => {
            try {
              const res = await auth.get("/owner/audit-certificate", {
                responseType: "blob",
              });
              const blob = new Blob([res.data], { type: "text/markdown;charset=utf-8" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
              a.download = `mindstein-audit-${ts}.md`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
              toast.success("AUDIT_CERTIFICATE downloaded");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Audit cert download failed");
            }
          }}
          className="ml-auto h-8 px-3 text-[11px] font-mono uppercase tracking-wider border border-amber-400 bg-amber-400 text-black hover:bg-amber-300 inline-flex items-center gap-2"
        >
          <FileText size={11} /> AUDIT_CERTIFICATE
        </button>
        <button
          data-testid="owner-download-handoff"
          onClick={async () => {
            try {
              const res = await auth.get("/owner/handoff-bundle", {
                responseType: "blob",
              });
              const blob = new Blob([res.data], { type: "application/zip" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
              a.download = `mindstein-handoff-${ts}.zip`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
              toast.success("HANDOFF_BUNDLE downloaded (.zip)");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Handoff bundle download failed");
            }
          }}
          className="h-8 px-3 text-[11px] font-mono uppercase tracking-wider border border-purple-400 bg-purple-400 text-black hover:bg-purple-300 inline-flex items-center gap-2"
        >
          <Package size={11} /> HANDOFF_BUNDLE
        </button>
        <button
          data-testid="owner-logout"
          onClick={logout}
          className="h-8 px-3 text-[11px] font-mono uppercase tracking-wider border border-[#27272A] hover:bg-[#141414] inline-flex items-center gap-2"
        >
          <LogOut size={11} /> LOGOUT_OWNER
        </button>
      </header>

      {/* stats */}
      <section className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Stat label="TOTAL_USERS" value={stats.data?.total_users ?? "—"} icon={Users} color="text-emerald-300" />
        <Stat label="BOT_MESSAGES" value={stats.data?.total_bot_messages ?? "—"} icon={Zap} color="text-amber-300" />
        <Stat label="TOPUP_PURCHASES" value={stats.data?.total_topup_purchases ?? "—"} color="text-purple-300" />
        <Stat label="ELITE_USERS" value={stats.data?.by_plan?.elite ?? 0} color="text-purple-200" />
        <Stat label="FRANCHISE_USERS" value={stats.data?.by_plan?.franchise ?? 0} color="text-rose-300" />
      </section>

      {/* API KEYS — easy add & go */}
      <KeysManager />

      {/* users */}
      <section className="border border-[#27272A] overflow-x-auto">
        <div className="px-3 py-2 border-b border-[#27272A] text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500">
          USERS · {users.data?.length ?? 0}
        </div>
        <table className="w-full text-[11px] font-mono min-w-[1100px]">
          <thead className="text-zinc-500 bg-[#0E0E0E]">
            <tr className="border-b border-[#27272A]">
              <th className="text-left p-2 uppercase">EMAIL</th>
              <th className="text-left p-2 uppercase">NAME</th>
              <th className="text-left p-2 uppercase">ROLE</th>
              <th className="text-left p-2 uppercase">PLAN</th>
              <th className="text-right p-2 uppercase">MONTHLY</th>
              <th className="text-right p-2 uppercase">BONUS</th>
              <th className="text-right p-2 uppercase">TOPUP</th>
              <th className="text-right p-2 uppercase">REMAINING</th>
              <th className="text-right p-2 uppercase">STREAK</th>
              <th className="text-left p-2 uppercase">SET_PLAN</th>
              <th className="text-left p-2 uppercase">GRANT</th>
            </tr>
          </thead>
          <tbody>
            {(users.data || []).map((u) => {
              const s = u.account_summary || {};
              return (
                <tr key={u.user_id} data-testid={`owner-user-row-${u.user_id}`} className="border-b border-[#161616] hover:bg-[#0E0E0E]">
                  <td className="p-2 text-white">{u.email}</td>
                  <td className="p-2 text-zinc-400">{u.name || "—"}</td>
                  <td className="p-2 uppercase text-zinc-400">{u.role}</td>
                  <td className="p-2 uppercase text-amber-300">{s.plan_id}</td>
                  <td className="p-2 text-right tabnum">{s.monthly_used}/{s.monthly_quota}</td>
                  <td className="p-2 text-right tabnum text-rose-300">{s.bonus_balance}</td>
                  <td className="p-2 text-right tabnum text-purple-300">{s.topup_balance}</td>
                  <td className="p-2 text-right tabnum text-emerald-300">{s.total_remaining}</td>
                  <td className="p-2 text-right tabnum">{s.consecutive_months}</td>
                  <td className="p-2">
                    <select
                      data-testid={`owner-setplan-${u.user_id}`}
                      defaultValue={s.plan_id}
                      onChange={(e) =>
                        setPlan.mutate({ user_id: u.user_id, plan_id: e.target.value })
                      }
                      className="h-7 bg-[#0A0A0A] border border-[#27272A] px-1 text-[10px] font-mono text-white"
                    >
                      {PLAN_IDS.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </td>
                  <td className="p-2">
                    <button
                      data-testid={`owner-grant-${u.user_id}`}
                      onClick={() => grant.mutate({ user_id: u.user_id, qty: 10 })}
                      className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-amber-400 text-amber-200 hover:bg-amber-400 hover:text-black"
                    >
                      +10
                    </button>
                  </td>
                </tr>
              );
            })}
            {users.data?.length === 0 && (
              <tr><td colSpan={11} className="p-6 text-center text-zinc-500">NO_USERS</td></tr>
            )}
          </tbody>
        </table>
      </section>

      <VoucherPanel />
    </div>
  );
}

function VoucherPanel() {
  const [count, setCount] = useState(5);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const vouchers = useQuery({
    queryKey: ["owner-vouchers"],
    queryFn: () => auth.get("/promos/vouchers").then((r) => r.data),
  });

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await auth.post("/promos/vouchers/generate", { count });
      toast.success(`GENERATED_${data.generated}_VOUCHERS`, {
        description: data.codes.slice(0, 3).join(" · ") + (data.generated > 3 ? " …" : ""),
      });
      qc.invalidateQueries({ queryKey: ["owner-vouchers"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "generate failed");
    } finally {
      setBusy(false);
    }
  };

  const rows = vouchers.data?.vouchers || [];
  return (
    <section className="border border-emerald-400/30" data-testid="owner-voucher-panel">
      <div className="flex flex-wrap items-center gap-3 px-3 py-2 border-b border-[#27272A] bg-[#0E0E0E]">
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-300">
          VOUCHERS · $9.99 / +10 USES EACH
        </span>
        <div className="ml-auto flex items-center gap-2">
          <input
            data-testid="voucher-gen-count"
            type="number"
            min={1}
            max={100}
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
            className="w-16 h-8 bg-[#0A0A0A] border border-[#27272A] px-2 text-[12px] font-mono text-white"
          />
          <button
            data-testid="voucher-gen-btn"
            onClick={generate}
            disabled={busy}
            className="h-8 px-3 border border-emerald-400 text-emerald-300 font-mono text-[10px] uppercase tracking-wider hover:bg-emerald-400 hover:text-black disabled:opacity-50"
          >
            GENERATE
          </button>
        </div>
      </div>
      <div className="max-h-[260px] overflow-y-auto">
        {rows.map((v) => (
          <div key={v.code} className="flex items-center gap-3 px-3 py-1.5 border-b border-[#161616] text-[11px] font-mono">
            <button
              onClick={() => { navigator.clipboard.writeText(v.code); toast.success("COPIED"); }}
              className="text-white hover:text-emerald-300"
              title="copy"
            >
              {v.code}
            </button>
            <span className={v.status === "active" ? "text-emerald-400" : "text-zinc-600"}>
              {v.status.toUpperCase()}
            </span>
            {v.redeemed_by && <span className="text-zinc-600 truncate">→ {v.redeemed_by}</span>}
          </div>
        ))}
        {rows.length === 0 && (
          <div className="p-4 text-center text-[11px] font-mono text-zinc-600">NO_VOUCHERS_YET</div>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value, sub, color, icon: Icon }) {
  return (
    <div className="border border-[#27272A] bg-[#0E0E0E] p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono inline-flex items-center gap-2">
        {Icon && <Icon size={10} />} {label}
      </div>
      <div className={"text-2xl font-mono tabnum mt-1 " + (color || "text-white")}>{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 font-mono mt-1">{sub}</div>}
    </div>
  );
}
